import { type DefaultRootState } from '@databases/redux';
import {
  type Database,
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue,
  ImportedFileType,
} from '@databases/types/database';
import {
  convertFromCsv,
  convertType,
  getInitialValue,
  isEmptyValue,
  parseFromHtml,
} from '@databases/utils/database/field-types';
import {
  domTreeIterator,
  getTextContent,
  replaceBlankCharacters,
} from '@databases/utils/lib/dom';
import { type Cheerio } from 'cheerio';
import { type Element, type Node } from 'domhandler';
import { parse } from 'papaparse';

import { resolveEntryReference, resolveFieldReference } from '../helpers/paste-helper';
import { type ImportResult, type ImportSettings, type ParsedPasteFile } from '../types';

function generateColumnsArray(data: (Cheerio<Element> | undefined)[][] | string[][]) {
  const numberOfColumns = data.reduce((res, row) => Math.max(row.length, res), 0);
  return [...new Array(numberOfColumns)];
}

function processTextContent(
  cell: Cheerio<Element> | undefined,
  fieldType: DatabaseFieldType | undefined
): string {
  if (!cell) {
    return '';
  }

  if (fieldType !== DatabaseFieldType.USER) {
    return getTextContent(cell, (node: Node) => replaceBlankCharacters(node, false));
  }

  let textContent = '';

  for (const { type, node } of domTreeIterator(cell)) {
    if (type === 'open') {
      const text = replaceBlankCharacters(node);

      if (text) {
        textContent ? (textContent += ',' + text) : (textContent = text);
      }
    }
  }
  return textContent;
}

function isTargetEmpty({
  field,
  database,
  value,
  state,
  text,
}: {
  field: DatabaseField | undefined;
  database: Database;
  value: DatabaseValue;
  state: DefaultRootState;
  text: string;
}) {
  return field ? isEmptyValue({ database, field, value }, state) : !text;
}

/**
 * Takes an imported file generated by parseImportFile() and parses it according to the specified import settings.
 */
export function applyImportSettings(
  database: Database,
  file: ParsedPasteFile,
  settings: ImportSettings,
  state: DefaultRootState
):
  | { success: true; result: ImportResult }
  | {
      success: false;
      error: { tag: 'UNKNOWN_FILE_TYPE'; fileType: string };
    } {
  switch (file.type) {
    case ImportedFileType.HTML: {
      const colsArr = generateColumnsArray(file.content);
      const rows: ImportResult['rows'] = file.content.map((row, rowIdx) => {
        const entry = resolveEntryReference(database, settings.entryReferences[rowIdx]);
        return {
          isSelected: settings.selectedRows[rowIdx] ?? true,
          cols: colsArr.map((_, i) => {
            const cell = row[i];
            const field = resolveFieldReference(database, settings.fieldReferences[i]);
            let isSourceEmpty: boolean | undefined;
            let value;

            if (field) {
              if (cell) {
                value = parseFromHtml(cell, { database, field, entry: entry! }, state);
                isSourceEmpty = value.isSourceEmpty;
              } else {
                value = getInitialValue(field.type);
              }
            }

            const isTargetEmpty = field
              ? !value || isEmptyValue({ database, field: field, value }, state)
              : true;
            const isTargetMulti = value?.value
              ? Array.isArray(value?.value) && value?.value.length > 1
              : false;
            return {
              text: processTextContent(cell, field?.type),
              value,
              isTargetEmpty,
              targetFieldId: field?.id,
              targetEntryId: entry?.id,
              targetIsMulti: isTargetMulti,
              isSourceEmpty,
            };
          }),
        };
      });
      return { success: true, result: { colCount: colsArr.length, rows } };
    }
    case ImportedFileType.CSV: {
      const data = parse<string[]>(file.content, { skipEmptyLines: true });
      const colsArr = generateColumnsArray(data.data);
      const rows: ImportResult['rows'] = data.data.map((row, rowIdx) => {
        const entry = resolveEntryReference(database, settings.entryReferences[rowIdx]);
        return {
          isSelected: settings.selectedRows[rowIdx] ?? true,
          cols: colsArr.map((_, i) => {
            const text = row[i] ?? '';
            const value: DatabaseValue = { type: DatabaseFieldType.TEXT, value: text };
            const field = resolveFieldReference(database, settings.fieldReferences[i]);
            const convertedValue = field
              ? convertFromCsv({ database, field, value }, field.type, state)
              : value;
            const isTargetMulti = Boolean(field?.settings.multi);
            return {
              text,
              value: convertedValue,
              isTargetEmpty: isTargetEmpty({ field, database, value, state, text }),
              targetFieldId: field?.id,
              targetEntryId: entry?.id,
              targetIsMulti: isTargetMulti,
              isSourceEmpty: undefined,
            };
          }),
        };
      });
      return { success: true, result: { colCount: colsArr.length, rows } };
    }
    case ImportedFileType.TEXT: {
      const text = file.content;
      const field = resolveFieldReference(database, settings.fieldReferences[0]);
      const entry = resolveEntryReference(database, settings.entryReferences[0]);
      const value: DatabaseValue = { type: DatabaseFieldType.TEXT, value: text };
      const convertedValue = field
        ? convertType({ field, value }, field.type, state)
        : undefined;

      const isTargetMulti = Boolean(field?.settings.multi);
      const rows: ImportResult['rows'] = [
        {
          isSelected: true,
          cols: [
            {
              text,
              value: convertedValue || value,
              isTargetEmpty: isTargetEmpty({
                field,
                database,
                value: convertedValue || value,
                state,
                text,
              }),
              targetFieldId: field?.id,
              targetEntryId: entry?.id,
              targetIsMulti: isTargetMulti,
              isSourceEmpty: undefined,
            },
          ],
        },
      ];

      return { success: true, result: { colCount: 1, rows } };
    }

    default:
      return {
        success: false,
        error: { tag: 'UNKNOWN_FILE_TYPE', fileType: (file as any).type },
      };
  }
}
